import { formatNumber } from "@/utils/numberFormat";
import { parseLocaleNumericInput } from "@/utils/numbers";
import { getCurrentLocale } from "@/utils/locale";

// A contract's FX rate converts its own currency into the account's:
// 1 unit of the contract currency is worth `rate` units of the account
// currency. Shared by the contract header and the contracts table so both
// render and accept a rate the same way.

// Four decimals reads well for most pairs (1 AUD = 0.7030 USD). A rate below
// 0.01 — a weak currency against a strong one, such as JPY into USD — would
// round to a couple of significant digits, so it gets six.
const getFxRatePrecision = (rate: number): number =>
  Math.abs(rate) < 0.01 ? 6 : 4;

/** A rate as `1 AUD = 0.7030 USD`, in the viewer's locale. */
export const formatFxRate = (
  rate: number,
  contractCurrency: string,
  accountCurrency: string,
  locale: string = getCurrentLocale()
): string => {
  const { value } = formatNumber(
    rate,
    getFxRatePrecision(rate),
    false,
    false,
    true,
    locale
  );
  return `1 ${contractCurrency} = ${value} ${accountCurrency}`;
};

// Digits with at most one decimal point, after the locale's separators have
// been read. Anything else — a sign, an exponent, a stray letter — is refused
// rather than left to Number(), which accepts " 1e3 " and "0x10".
const CANONICAL_RATE_PATTERN = /^(\d+\.?\d*|\.\d+)$/;

// A lone comma the locale reads as grouping is only taken as grouping when it
// is shaped like it. "0,7030" or "1,5" typed in en-US is a comma decimal, and
// reading it as grouping would save a rate ten thousand or ten times too big.
const LONE_COMMA_GROUPING_SHAPE = /^[1-9]\d{0,2},\d{3}$/;

// The mirror case: where the comma is the decimal, "1.350" is a thousands
// group to the reader and 1.35 to the parser, which always reads a lone dot as
// the decimal. Reading it that way would save a rate a thousand times too
// small, so it is refused there instead.
const LONE_DOT_GROUPING_SHAPE = /^[1-9]\d{0,2}\.\d{3}$/;

const usesCommaDecimal = (locale: string): boolean =>
  new Intl.NumberFormat(locale)
    .formatToParts(1.1)
    .find((part) => part.type === "decimal")?.value === ",";

/**
 * A rate as the editor shows it: the locale's decimal separator, no grouping
 * and every stored digit, so a comma-decimal viewer edits "1,234" rather than
 * a dot this parser would refuse as ambiguous.
 */
export const formatFxRateForEditing = (
  rate: number,
  locale: string = getCurrentLocale()
): string => {
  // toString keeps the shortest digits that round-trip; Intl with a wide
  // fraction would print the double's binary tail (0.1 as 0.1000…0555).
  const canonical = rate.toString();
  return usesCommaDecimal(locale) ? canonical.replace(".", ",") : canonical;
};

/**
 * The rate a user typed, or null when it is not a usable rate: blank, not a
 * number, zero or negative. A null here must block the save — the server
 * stores whatever positive number it is sent, and 0 would zero every
 * account-currency figure on the contract.
 */
export const parseFxRateInput = (
  input: string,
  locale: string = getCurrentLocale()
): number | null => {
  const trimmed = input.trim();
  if (trimmed === "") return null;
  const canonical = parseLocaleNumericInput(trimmed, locale);
  if (!CANONICAL_RATE_PATTERN.test(canonical)) return null;
  const readCommaAsGrouping =
    /^[^.,]*,[^.,]*$/.test(trimmed) && !canonical.includes(".");
  if (readCommaAsGrouping && !LONE_COMMA_GROUPING_SHAPE.test(trimmed)) {
    return null;
  }
  if (LONE_DOT_GROUPING_SHAPE.test(trimmed) && usesCommaDecimal(locale)) {
    return null;
  }
  const rate = Number(canonical);
  return Number.isFinite(rate) && rate > 0 ? rate : null;
};
